import { useMemo, useState } from 'react'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { Button } from '../Common/Button'
import { IconButton } from '../Common/IconButton'
import { usePreviewGuard, requestPreviewGraduation } from '../../hooks/usePreviewGuard'
import './viewerBanner.css'

const BANNER_DISMISS_KEY = 'timelining-viewer-banner-dismissed'

type ViewerBannerProps = {
  onRequestAccess?: () => void
  onTakeTour?: () => void
}

/** Persistent top strip visible on every preview (demo or public) workspace. */
export const ViewerBanner = ({ onRequestAccess, onTakeTour }: ViewerBannerProps) => {
  const { isPreview, previewKind, activeWorkspace } = usePreviewGuard()
  const [isDismissed, setIsDismissed] = useState(() => {
    try {
      return window.sessionStorage.getItem(BANNER_DISMISS_KEY) === '1'
    } catch {
      return false
    }
  })

  const message = useMemo(() => {
    if (!activeWorkspace) return ''
    if (previewKind === 'DEMO') {
      return `You're exploring the ${activeWorkspace.name} demo workspace.`
    }
    return `Viewing ${activeWorkspace.name} — a shared public workspace.`
  }, [activeWorkspace, previewKind])

  if (!isPreview || isDismissed) return null

  const handleDismiss = () => {
    try {
      window.sessionStorage.setItem(BANNER_DISMISS_KEY, '1')
    } catch {
      // ignore storage failures
    }
    setIsDismissed(true)
  }

  return (
    <div className="viewerBanner" data-preview-kind={previewKind ?? 'DEMO'} role="status" aria-live="polite">
      <div className="viewerBannerInner">
        <span className="viewerBannerPill">Viewer</span>
        <span className="viewerBannerMessage">{message}</span>
        <div className="viewerBannerActions">
          {previewKind === 'DEMO' && onTakeTour ? (
            <Button variant="tertiary" size="sm" onClick={onTakeTour} aria-label="Take a guided tour">
              Take the tour
            </Button>
          ) : null}
          {previewKind === 'DEMO' ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => requestPreviewGraduation()}
              aria-label="Switch to my workspace"
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>Switch to my workspace <ArrowForwardRoundedIcon fontSize="inherit" /></span>
            </Button>
          ) : null}
          {previewKind === 'PUBLIC' && onRequestAccess ? (
            <Button variant="primary" size="sm" onClick={onRequestAccess} aria-label="Request access to this workspace">
              Request access
            </Button>
          ) : null}
          <IconButton
            size="sm"
            variant="tertiary"
            label="Dismiss viewer banner"
            onClick={handleDismiss}
            icon={<CloseRoundedIcon fontSize="inherit" />}
          />
        </div>
      </div>
    </div>
  )
}
